import { useEffect, useState } from 'react';
import {
  fetchAlerts,
  fetchAlertsUnreadCount,
  markAlertAsRead,
  snoozeAlert,
  dismissAlert,
  confirmAlert,
  markAllAlertsAsRead,
  type Alert,
  type AlertFilters
} from '../api/alerts';
import { emitNotificationCountUpdate } from '../utils/notificationEvents';

export function useAlerts(filters?: AlertFilters) {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      // Filtres par défaut : exclure les alertes reportées et résolues
      const defaultFilters: AlertFilters = {
        exclude_snoozed: true,
        ...filters
      };

      const [list, count] = await Promise.all([
        fetchAlerts(defaultFilters),
        fetchAlertsUnreadCount().catch(() => 0)
      ]);

      setAlerts(list);
      setUnreadCount(count);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur de chargement des alertes');
      console.error('Erreur useAlerts:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id: number) => {
    try {
      const updated = await markAlertAsRead(id);
      setAlerts(prev => prev.map(a => (a.id === id ? updated : a)));
      if (updated.is_read) {
        setUnreadCount(c => Math.max(0, c - 1));
      }
    } catch (err) {
      console.error('Erreur markAsRead:', err);
    }
  };

  const snooze = async (id: number, days: number = 1) => {
    try {
      // Trouver l'alerte avant de la mettre à jour pour savoir si elle était non lue
      const alertBefore = alerts.find(a => a.id === id);
      const wasUnread = alertBefore && !alertBefore.is_read;

      const updated = await snoozeAlert(id, days);
      setAlerts(prev => prev.filter(a => a.id !== id));

      if (wasUnread) {
        setUnreadCount(c => Math.max(0, c - 1));
      }
      emitNotificationCountUpdate();
      return updated;
    } catch (err) {
      console.error('Erreur snooze:', err);
      throw err;
    }
  };

  const dismiss = async (id: number) => {
    try {
      // Trouver l'alerte avant de la mettre à jour pour savoir si elle était non lue
      const alertBefore = alerts.find(a => a.id === id);
      const wasUnread = alertBefore && !alertBefore.is_read;

      const updated = await dismissAlert(id);
      setAlerts(prev => prev.filter(a => a.id !== id));

      if (wasUnread) {
        setUnreadCount(c => Math.max(0, c - 1));
      }
      emitNotificationCountUpdate();
      return updated;
    } catch (err) {
      console.error('Erreur dismiss:', err);
      throw err;
    }
  };

  const confirm = async (id: number) => {
    try {
      // Trouver l'alerte avant de la mettre à jour pour savoir si elle était non lue
      const alertBefore = alerts.find(a => a.id === id);
      const wasUnread = alertBefore && !alertBefore.is_read;

      const updated = await confirmAlert(id);
      // Retirer l'alerte confirmée de la liste (comme pour snooze et dismiss)
      setAlerts(prev => prev.filter(a => a.id !== id));

      if (wasUnread) {
        setUnreadCount(c => Math.max(0, c - 1));
      }
      emitNotificationCountUpdate();
      return updated;
    } catch (err) {
      console.error('Erreur confirm:', err);
      throw err;
    }
  };

  const markAllAsRead = async () => {
    try {
      await markAllAlertsAsRead();
      setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Erreur markAllAsRead:', err);
    }
  };

  useEffect(() => {
    load();
    // Polling toutes les 30 secondes pour plus de réactivité
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  return {
    alerts,
    unreadCount,
    loading,
    error,
    markAsRead,
    snooze,
    dismiss,
    confirm,
    markAllAsRead,
    refetch: load
  };
}
